// Backend has started returning string fields as locale maps
// (e.g. `{ en: 'Hotel', es: 'Hotel', fr: 'Hôtel', ... }`). Components that
// render those fields directly into JSX crash with React error #31. These
// helpers collapse such maps to a single string at the boundary so the rest
// of the FE keeps treating them as strings.
//
// `flattenLocaleMaps` walks deeply through objects and arrays — use it for
// schema payloads (celebrationJson, notWorkingJson, discovery_json, etc.).
// `flattenLocaleMapsShallow` only walks the top level — use it where the
// schema is flat (tenantInfo).

const LOCALE_KEYS = new Set([
  'en', 'es', 'fr', 'de', 'it', 'pt', 'pt-BR', 'pt-PT',
  'zh', 'zh-CN', 'ja', 'ko', 'ru', 'nl', 'ar',
]);

export const looksLikeLocaleMap = (v: any): boolean => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const keys = Object.keys(v);
  if (keys.length === 0) return false;
  let hits = 0;
  for (const k of keys) if (LOCALE_KEYS.has(k)) hits += 1;
  // Require at least half the keys to be known locales — keeps random objects
  // (e.g. coordinate pairs `{lat, lng}`) from being mistaken for locale maps.
  return hits / keys.length >= 0.5;
};

export const pickLocaleString = (map: Record<string, any>, lang: string): string => {
  const base = lang ? lang.split('-')[0] : '';
  const candidate =
    map[lang] ??
    (base ? map[base] : undefined) ??
    map.en ??
    Object.values(map).find((v) => typeof v === 'string');
  return typeof candidate === 'string' ? candidate : '';
};

export function flattenLocaleMapsShallow<T extends Record<string, any>>(input: T, lang: string): T {
  if (!input || typeof input !== 'object') return input;
  const out: Record<string, any> = Array.isArray(input) ? [...input] : { ...input };
  for (const key of Object.keys(out)) {
    const v = out[key];
    if (looksLikeLocaleMap(v)) out[key] = pickLocaleString(v, lang);
  }
  return out as T;
}

export function flattenLocaleMaps<T>(input: T, lang: string): T {
  if (input == null) return input;
  if (looksLikeLocaleMap(input)) return pickLocaleString(input as any, lang) as any;
  if (Array.isArray(input)) return input.map((item) => flattenLocaleMaps(item, lang)) as any;
  if (typeof input === 'object') {
    const out: Record<string, any> = {};
    for (const key of Object.keys(input as any)) {
      out[key] = flattenLocaleMaps((input as any)[key], lang);
    }
    return out as T;
  }
  return input;
}
